import { FormattedEvent } from '@eventbrite/event-renderer';
import { getWindowObject } from '@eventbrite/feature-detection';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { requestIdleCallbackWithPolyfill } from '@eventbrite/request-idle-callback';
import { logEvent } from '@eventbrite/statsig';
import { fitBounds } from 'google-map-react';
import debounce from 'lodash/debounce';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import SuperCluster from 'supercluster';
import { hasSameEvents } from '../../utils/events';
import { CenterPoint } from './CenterPoint';
import {
    DEFAULT_ZOOM_VALUE,
    GLOBAL_BBOX,
    MAX_ZOOM,
    MAX_ZOOM_WHEN_SEARCHING,
} from './constants';
import InteractiveMapMarker from './interactiveMapMarker/InteractiveMapMarker';
import './searchMap.scss';
import {
    calculateAverageCenter,
    convertEventsToGeoJSON,
    getBoundsFromEventLatLngs,
    getLatLngsFromEvents,
} from './utils';

interface SearchMapProps {
    events: FormattedEvent[];
    latitude: number;
    longitude: number;
    setShouldScroll?: Function;
    searchByBBox?: Function;
    hoverEventCardTarget?: React.ReactNode;
    onSaveEvent?: Function;
    isAuthenticated?: boolean;
    isFixed?: boolean;
    isLoading?: boolean;
    height?: number | string;
    width?: number | string;
    countryCode?: string;
    googleMapLanguage?: string;
    setMapPinHoverTarget: Function;
    setMapPinClickTarget: Function;
    selectedPinEvent?: string;
    userLatLng: GeolocationCoordinates | null;
    isUsingCurrentLocation: boolean;
}

type map = {
    maxZoom: number;
    setOptions: (arg0: { maxZoom: number }) => void;
    fitBounds: (arg0: any) => void;
};

type maps = {
    LatLngBounds: new () => any;
    Marker: new (arg0: { lat: number; lng: number }) => any;
};

const withSearchMapBase = (SearchMapVariant: typeof React.Component) =>
    class SearchMap extends React.PureComponent<SearchMapProps> {
        static defaultProps = {
            height: '100vh',
            width: '100%',
        };

        clusterIndex: {
            load: Function;
            getClusters: Function;
            getLeaves: Function;
        };
        smartZoom?: Function;
        state: {
            bounds: {
                sw: { lat: number; lng: number };
                ne: { lat: number; lng: number };
            };
            mapBoundsChangeCount: number;
            zoom: number;
            tooltipSelectedEventsIds?: string[];
            center: { lat?: number; lng?: number };
            hasInitialZoom: boolean;
            googleApisLoaded: boolean;
        };

        constructor(props: Readonly<SearchMapProps>) {
            super(props);

            this.clusterIndex = new SuperCluster({
                radius: 5,
                maxZoom: MAX_ZOOM,
            });

            this.clusterIndex.load(convertEventsToGeoJSON(props.events || []));
            const zoom = DEFAULT_ZOOM_VALUE;

            let center = {
                lat: props.latitude || 0,
                lng: props.longitude || 0,
            };

            if (!props.latitude || !props.longitude) {
                center = calculateAverageCenter(
                    this.clusterIndex.getClusters(GLOBAL_BBOX, zoom),
                    center,
                );
            }

            this.state = {
                hasInitialZoom: false,
                googleApisLoaded: false,
                zoom,
                bounds: {
                    // Note that by the time the user sees anything,
                    // these will already be set to the correct bounds
                    // because _handleBoundsChange gets automatically
                    // called once by the library during the initial mount
                    ne: { lat: 90, lng: 180 },
                    sw: { lat: -90, lng: -180 },
                },
                mapBoundsChangeCount: 0,
                center,
                tooltipSelectedEventsIds: [],
            };
        }

        componentDidMount() {
            const { setShouldScroll, events } = this.props;

            if (setShouldScroll) {
                setShouldScroll?.(events.length);
            }

            const addEventListener = getWindowObject('addEventListener');
            this.calculateInitialZoom();

            if (addEventListener) {
                addEventListener('resize', this._debounceRefitMap);
            }
        }

        componentDidUpdate(prevProps: Readonly<SearchMapProps>) {
            if (!isEmpty(this.props.events)) {
                this.clusterIndex.load(
                    convertEventsToGeoJSON(this.props.events),
                );
                if (
                    this.smartZoom &&
                    !hasSameEvents(prevProps.events, this.props.events)
                ) {
                    this.smartZoom(this.props.events);
                }
            }
            if (
                prevProps.latitude !== this.props.latitude &&
                prevProps.longitude !== this.props.longitude
            ) {
                const center = {
                    lat: this.props.latitude,
                    lng: this.props.longitude,
                };
                this._setCenter(center);
            }
        }

        calculateInitialZoom() {
            if (!isEmpty(this.props.events)) {
                const latlngs = getLatLngsFromEvents(this.props.events);
                const bounds = getBoundsFromEventLatLngs(latlngs);
                const elementSize = document
                    .querySelector('[data-id=search-map-panel]')
                    ?.getBoundingClientRect();

                const { zoom, center } = fitBounds(bounds, {
                    height: elementSize?.height || 0,
                    width: elementSize?.width || 0,
                });

                this.setState({ zoom, center });
            }

            this.setState({ hasInitialZoom: true });
        }

        _setCenter = (center: { lat?: number; lng?: number }) => {
            this.setState({
                center,
                zoom: DEFAULT_ZOOM_VALUE,
            });
        };

        componentWillUnmount() {
            const removeEventListener = getWindowObject('removeEventListener');

            if (removeEventListener) {
                removeEventListener('resize', this._debounceRefitMap);
            }
        }

        _handleBoundsChange = ({
            zoom,
            bounds,
        }: {
            zoom: number;
            bounds: { [key: string]: { lat: number; long: number } };
        }) => {
            // This variable is intended to track when the map bounds are changed at the same zoom level,
            // so we only increment if the zoom level stays the same.
            // eslint-disable-next-line react/no-access-state-in-setstate
            const mapBoundsChangeCount = this.state.mapBoundsChangeCount + 1;
            this.setState({
                zoom,
                bounds,
                mapBoundsChangeCount,
            });
        };

        _handleSearchByBounds = () => {
            this.props.searchByBBox?.(this.state.bounds);
        };

        _handleMapMarkerClick = (eventsId: string[]) => {
            this.props.setMapPinClickTarget(eventsId);

            logEvent('Search - Map - Click - Marker');
        };

        _handleClickOutsideMapMarker = () => {
            this.setState({
                tooltipSelectedEventsIds: [],
            });
        };

        _getEventIdFromEventProps = (eventsId: string) => {
            let selectedId;

            if (!isEmpty(eventsId)) {
                const id = first(eventsId);

                selectedId = id;
            }

            return selectedId;
        };

        _getFirstEventClusterEventIds = () => {
            let firstEventCluster: { eventsId?: string } | undefined;
            const markerData = this._getEventMarkerData();

            if (markerData.length) {
                firstEventCluster = first(markerData);
            }

            return firstEventCluster && firstEventCluster.eventsId;
        };

        _getEventMarkerData = () => {
            // Turns an array of single points and clusters into an array of map markers.
            // Clusters represent groups of events that are very close to each other on the map,
            // which is determined by pixel radius.
            const { bounds, zoom, tooltipSelectedEventsIds } = this.state;
            const {
                hoverEventCardTarget,
                onSaveEvent,
                isAuthenticated,
                isFixed,
                selectedPinEvent,
            } = this.props;

            let clusters = this.clusterIndex.getClusters(
                [bounds.sw.lng, bounds.sw.lat, bounds.ne.lng, bounds.ne.lat],
                zoom,
            );

            // sort by latitude so that the markers look orderly on the map
            clusters = clusters.sort(
                (
                    a: { geometry: { coordinates: number[] } },
                    b: { geometry: { coordinates: number[] } },
                ) => b.geometry.coordinates[1] - a.geometry.coordinates[1],
            );

            return clusters.map(
                ({
                    geometry,
                    properties,
                }: {
                    geometry: {
                        coordinates: string[];
                    };
                    properties: {
                        id: string;
                        name: string | GenericLazyString;
                        cluster: any;
                        point_count: number;
                        cluster_id: string;
                    };
                }) => {
                    const lng = parseFloat(geometry.coordinates[0]);
                    const lat = parseFloat(geometry.coordinates[1]);

                    let markerProps = {
                        lat,
                        lng,
                        label: properties.id,
                        name: properties.name,
                        hoverEventCardTarget,
                        selectedPinEvent,
                        onSaveEvent,
                        isAuthenticated,
                        parentIsFixed: isFixed,
                    };

                    let eventProps = [properties];

                    if (properties && properties.cluster) {
                        let clusterName = gettext(
                            'Zoom to see %(eventCount)s events',
                            { eventCount: properties.point_count },
                        );
                        const clusterChildren = this.clusterIndex.getLeaves(
                            properties.cluster_id,
                            20,
                        );

                        if (properties.point_count === 2 || zoom === MAX_ZOOM) {
                            // In some cases, for example when points share the same address (like when they're at the same hotel),
                            // the clusters will never be split up into single points. Therefore, we need some logic to
                            // ensure that the user can still see the names.

                            clusterName = clusterChildren
                                .map(
                                    ({
                                        properties: childProperties,
                                    }: {
                                        properties: { name: string };
                                    }) => childProperties.name,
                                )
                                .join(' & ');
                        }

                        markerProps = {
                            ...markerProps,
                            label: properties.id,
                            name: clusterName,
                        };

                        eventProps = clusterChildren.map(
                            (clusterChild: { properties: any }) =>
                                clusterChild.properties,
                        );
                    }

                    const eventsId = eventProps.map(({ id }) => id);

                    return {
                        ...markerProps,
                        eventsId,
                        tooltipSelectedEventsIds: tooltipSelectedEventsIds,
                        key:
                            properties.id || `cluster-${properties.cluster_id}`,
                        onMapMarkerClick: this._handleMapMarkerClick,
                    };
                },
            );
        };

        _getEventMarkers = () =>
            this._getEventMarkerData().map(
                (markerData: JSX.IntrinsicAttributes) => (
                    //eslint-disable-next-line
                    <InteractiveMapMarker {...markerData} />
                ),
            );

        _createSmartZoom =
            (map: map, maps: maps) => (events: FormattedEvent[]) => {
                if (!events || !events.length) {
                    return;
                }

                if (!map || !maps) {
                    return;
                }

                const bounds = new maps.LatLngBounds();
                const latLngs = getLatLngsFromEvents(events);

                latLngs.forEach((latLng) => {
                    if (
                        typeof latLng.lat === 'number' &&
                        typeof latLng.lng === 'number'
                    ) {
                        bounds.extend(
                            new maps.Marker({
                                lat: latLng.lat,
                                lng: latLng.lng,
                            }),
                        );
                    }
                });
                const originalMaxZoom = map.maxZoom;
                map.setOptions({ maxZoom: MAX_ZOOM_WHEN_SEARCHING });
                map.fitBounds(bounds);
                map.setOptions({ maxZoom: originalMaxZoom });
            };

        _refitMap = () => {
            const { events } = this.props;

            if (this.smartZoom) {
                this.smartZoom(events);
            }
        };

        _debounceRefitMap = debounce(this._refitMap, 500);

        _handleGoogleApiLoaded = ({ map, maps }: { map: map; maps: maps }) => {
            requestIdleCallbackWithPolyfill(() => {
                this.smartZoom = this._createSmartZoom(map, maps);
            });
            this.setState({ googleApisLoaded: true });
        };

        static getDerivedStateFromProps(props: {
            events: FormattedEvent[];
            searchMapBounds: { split: (arg0: string) => any[] };
            width?: number;
            height?: number;
        }) {
            if (isEmpty(props.events) && props.searchMapBounds) {
                const [swLong, swLat, neLong, neLat] =
                    props.searchMapBounds.split(',');
                const bounds = {
                    ne: {
                        lat: parseFloat(neLat),
                        lng: parseFloat(neLong),
                    },
                    sw: {
                        lat: parseFloat(swLat),
                        lng: parseFloat(swLong),
                    },
                };
                const size = {
                    width: props.width || 0,
                    height: props.height || 0,
                };
                const { center } = fitBounds(bounds, size);

                return { center };
            }
            return null;
        }

        getMapMarkers() {
            const {
                events,
                latitude,
                longitude,
                userLatLng,
                isUsingCurrentLocation,
            } = this.props;

            let markers = [
                <CenterPoint
                    key="center"
                    label="center-point"
                    name="center-point"
                    lat={userLatLng?.latitude || latitude}
                    lng={userLatLng?.longitude || longitude}
                    isUsingCurrentLocation={isUsingCurrentLocation}
                />,
            ];

            if (events.length) {
                markers = [...markers, ...this._getEventMarkers()];
            }

            return markers;
        }

        render() {
            const { height, width, countryCode, googleMapLanguage } =
                this.props;

            const { zoom, center } = this.state;
            const markers = this.getMapMarkers();

            // For passing to the actual EDS Interactive Map component
            // (just for organizational purposes)
            const propsForInteractiveMap = {
                height,
                width,
                language: googleMapLanguage,
                region: countryCode,
                center,
                zoom,
                onChange: this._handleBoundsChange,
                onGoogleApiLoaded: this._handleGoogleApiLoaded,
                yesIWantToUseGoogleMapApiInternals: true,
            };

            return (
                <SearchMapVariant
                    {...this.props}
                    {...this.state}
                    onSearchByBounds={this._handleSearchByBounds}
                    onClickOutsideMapMarker={this._handleClickOutsideMapMarker}
                    markers={markers}
                    shouldMountMap={this.state.hasInitialZoom}
                    shouldShowMap={this.state.googleApisLoaded}
                    propsForInteractiveMap={propsForInteractiveMap}
                />
            );
        }
    };

export default withSearchMapBase;
